/**
 * Les OPTIONS d'une question de questionnaire — et la case « Autre » qui laisse
 * le client écrire ce que la liste n'a pas prévu.
 *
 * POURQUOI CE MODULE EXISTE. Une option est stockée en JSON libre et prend deux
 * formes selon son âge : une chaîne nue (« Oui ») pour tout ce qui a été créé
 * avant, un objet `{ label, value }` depuis. Chaque écran qui affiche une
 * question avait donc sa propre paire `getOptionValue`/`getOptionLabel`,
 * recopiée à l'identique — cinq fois dans le dépôt, plus le PDF côté serveur.
 * La case « Autre » ajoute une troisième forme : recopier une sixième règle
 * dans chacun d'eux était le moyen sûr qu'un écran l'oublie et affiche
 * « __autre__ » au client.
 *
 * COMMENT « AUTRE » EST STOCKÉE. C'est une option comme les autres, en dernière
 * position, avec une valeur réservée : `{ label, value: '__autre__', autre: true }`.
 * Pas de colonne dédiée en base, et c'est délibéré — « Autre » EST un choix que
 * le client lit dans la liste, au même rang que les autres. Un booléen posé à
 * côté d'`options` aurait obligé chaque renderer à recomposer la liste qu'il
 * affiche, et à s'accorder sur la position et le libellé de la case.
 *
 * OÙ VA LE TEXTE LIBRE. Dans la colonne `answer` de la réponse, à côté du
 * `selectedOptions` qui porte le choix. Les deux cohabitent : `selectedOptions`
 * dit QUE le client a choisi « Autre », `answer` dit QUOI. C'est la seule
 * question à choix où les deux colonnes sont remplies en même temps.
 *
 * Module partagé front/back : aucune dépendance à Vue ni à Prisma.
 */

/**
 * La valeur réservée de la case « Autre ». Réservée : une option ordinaire ne
 * peut pas la porter, le formulaire d'édition n'écrit que des libellés.
 */
export const VALEUR_AUTRE = '__autre__'

/** Ce que la case propose tant que personne ne l'a renommée. */
export const LIBELLE_AUTRE_PAR_DEFAUT = 'Autre (précisez)'

/** Longueur maximale du texte libre saisi derrière « Autre ». */
export const TEXTE_AUTRE_MAX = 500

/** La valeur enregistrée quand cette option est choisie. */
export function valeurOption(option) {
  if (option && typeof option === 'object') return option.value ?? option.label ?? ''
  return option ?? ''
}

/** Le libellé que le client lit. */
export function libelleOption(option) {
  if (option && typeof option === 'object') return option.label ?? option.value ?? ''
  return option ?? ''
}

/** Cette option est-elle la case « Autre » ? */
export function estOptionAutre(option) {
  if (!option || typeof option !== 'object') return false
  return option.autre === true || option.value === VALEUR_AUTRE
}

/** La case « Autre » d'une question, ou `null` si elle n'en a pas. */
export function optionAutreDe(question) {
  const options = Array.isArray(question?.options) ? question.options : []
  return options.find(estOptionAutre) || null
}

/**
 * La case « Autre » est-elle CHOISIE dans cette réponse ? C'est ce qui décide
 * d'afficher le champ de texte libre, et de l'imprimer dans le récapitulatif.
 */
export function autreEstChoisie(question, reponse) {
  if (!optionAutreDe(question)) return false
  const choisies = Array.isArray(reponse?.selectedOptions) ? reponse.selectedOptions : []
  return choisies.includes(VALEUR_AUTRE)
}

/**
 * La case « Autre » se propose sur les questions à réponse UNIQUE, et pas sur
 * les cases à cocher : une réponse libre y voisinerait avec d'autres réponses
 * cochées, et rien dans la colonne `answer` ne dirait à laquelle elle se
 * rattache. Les cases à cocher ont pour cela une question de texte à part.
 */
export function accepteAutre(type) {
  return type === 'radio' || type === 'select'
}

/** Le texte libre d'une réponse, borné et débarrassé de ses espaces. */
export function texteAutre(reponse) {
  const texte = reponse?.answer
  if (typeof texte !== 'string') return ''
  return texte.trim().slice(0, TEXTE_AUTRE_MAX)
}

/** Les types dont la réponse se choisit dans une liste d'options. */
export function estTypeAChoix(type) {
  return type === 'radio' || type === 'select' || type === 'checkbox'
}

/**
 * Ce qu'un écran de saisie vient d'émettre, ramené aux DEUX colonnes de la
 * réponse : `selectedOptions` (ce qui a été choisi) et `answer` (ce qui a été
 * écrit). Les deux écrans qui reçoivent cette saisie — le tunnel du commercial
 * et le questionnaire du client — appellent cette fonction plutôt que de
 * décider chacun de son côté : c'est la case « Autre » qui l'a rendu
 * nécessaire, puisqu'elle est la seule à remplir les deux à la fois.
 *
 * @param {*} valeur ce que le renderer a émis : une chaîne, un tableau de
 *   valeurs choisies, ou `{ selectedOptions, answer }` pour une case « Autre »
 * @param {string} type le type de la question
 */
export function reponseDepuisSaisie(valeur, type) {
  if (!estTypeAChoix(type)) return { answer: valeur, selectedOptions: [] }

  if (valeur && typeof valeur === 'object' && !Array.isArray(valeur)) {
    const texte = typeof valeur.answer === 'string' ? valeur.answer.slice(0, TEXTE_AUTRE_MAX) : null
    return {
      answer: texte && texte.trim() ? texte : null,
      selectedOptions: nettoyerChoix(valeur.selectedOptions),
    }
  }

  return { answer: null, selectedOptions: nettoyerChoix(valeur) }
}

/**
 * Les valeurs réellement choisies. LA CHAÎNE VIDE EN EST EXCLUE : c'est ce que
 * rend une liste déroulante remise sur « Sélectionner… », et l'enregistrer
 * aurait fait passer la question pour répondue auprès du contrôle des champs
 * obligatoires.
 */
function nettoyerChoix(valeur) {
  const liste = Array.isArray(valeur) ? valeur : [valeur]
  return liste.filter((v) => v !== null && v !== undefined && String(v) !== '')
}
